import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import sortBy from "lodash/sortBy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock as faLockSolid,
  faClock,
} from "@fortawesome/pro-solid-svg-icons";

import { useRoutes } from "utils/routes";
import Loading from "ui/Loading";
import SQL from "ui/SQL";
import Grid, { CellData } from "ui/Grid";
import Button from "ui/Button";
import Badge from "ui/Badge";
import { useFeature } from "providers/OrganizationFeatures";

import {
  BackendListActivityQuery,
  useBackendListActivityQuery,
} from "./gql/Query.generated";

import classNames from "classnames";
import { ExpandToggle } from "ui/Icons";

type BackendAtTimeType = BackendListActivityQuery["getBackends"][number];
type Backend = BackendAtTimeType["backend"];
type BackendSnapshot = BackendAtTimeType["snapshot"];
type VacuumRun = NonNullable<BackendAtTimeType["vacuumRun"]>;

type Props = {
  databaseId: string;
  serverId: string;
  timestamp: number;
  silentRefresh: boolean;
  setPaused?: (value: boolean) => void;
};

type BackendAtTimeGridType = {
  backend: Backend;
  pid: number;
  roleName?: string;
  waitingForLock: boolean;
  state: string;
  queryAge?: string;
  queryTextShort?: string;
  snapshot: BackendSnapshot;
  waitEventWithType: string;
  vacuumRun: VacuumRun | null;
};

const Activity: React.FunctionComponent<Props> = ({
  databaseId,
  serverId,
  timestamp,
  silentRefresh,
  setPaused,
}) => {
  const { data, previousData, loading, error } = useBackendListActivityQuery({
    variables: {
      databaseId,
      timestamp,
    },
  });

  const backendData = data?.getBackends ?? previousData?.getBackends;
  if ((loading && (!backendData || !silentRefresh)) || error) {
    return <Loading error={!!error} />;
  }

  const backends = sortBy(
    backendData,
    (b: BackendAtTimeType): number =>
      b.snapshot.queryStart ||
      (b.snapshot.state == "idle" && parseInt(b.backend.identity, 10)) ||
      (b.snapshot.state == "unknown" &&
        parseInt(b.backend.identity, 10) * 10) ||
      -parseInt(b.backend.identity, 10),
  ).map((b) => {
    return {
      backend: b.backend,
      pid: b.backend.pid,
      roleName: b.backend.postgresRole?.name,
      waitingForLock: b.snapshot.waitingForLock,
      state: b.snapshot.state,
      queryAge: b.snapshot.queryAge,
      queryTextShort: b.snapshot.queryTextShort,
      snapshot: b.snapshot,
      waitEventWithType: b.snapshot.waitEvent
        ? `${b.snapshot.waitEventType} / ${b.snapshot.waitEvent}`
        : "-",
      vacuumRun: b.vacuumRun ?? null,
    } satisfies BackendAtTimeGridType;
  });

  return (
    <WithPidFocus>
      <WithPidHover>
        <Grid
          data={backends}
          cellRenderer={CellRenderer}
          columns={[
            {
              field: "pid",
              header: "PID",
              width: "65px",
              style: "number",
              renderer: function PidCell({ rowData: { backend } }) {
                return (
                  <PidCellWithRef backend={backend} databaseId={databaseId} />
                );
              },
              className: "!overflow-visible",
            },
            {
              field: "roleName",
              header: "Role",
              nullValue: "-",
              width: "120px",
              renderer: function RoleCell({ fieldData }) {
                return (
                  <div
                    className="text-ellipsis overflow-hidden"
                    title={fieldData}
                  >
                    {fieldData}
                  </div>
                );
              },
            },
            {
              field: "state",
              header: "State",
              width: "120px",
              renderer: function StateCell({
                rowData: { state, waitingForLock },
              }) {
                if (waitingForLock) {
                  return state + " (waiting)";
                } else {
                  return state;
                }
              },
            },
            {
              field: "queryAge",
              header: "Query Age",
              width: "120px",
              tip: "The query age as of when the connection data was collected.",
            },
            {
              field: "queryTextShort",
              header: "Last Query",
              width: "1fr",
              renderer: function LastQueryCell({
                rowData: { snapshot, backend, vacuumRun },
              }) {
                return (
                  <QueryDetails
                    snapshot={snapshot}
                    backend={backend}
                    vacuumRun={vacuumRun}
                    serverId={serverId}
                    setPaused={setPaused}
                  />
                );
              },
            },
            {
              field: "waitEventWithType",
              header: "Wait Event",
              width: "200px",
              className: "whitespace-normal",
            },
          ]}
        />
      </WithPidHover>
    </WithPidFocus>
  );
};

const CellRenderer: React.ComponentType<
  CellData<BackendAtTimeGridType, "pid" | "snapshot"> & {
    row: number;
    column: number;
    title: string;
    className: string;
    children: React.ReactNode;
  }
> = ({ rowData, title, className, children }) => {
  const blocking = rowData.snapshot.uniqBlockingPids.length > 0;
  const blocked = rowData.snapshot.uniqBlockedByPids.length > 0;
  const pid = rowData.pid;
  const hoveredPid = useHoveredPid();
  const hasLockMonitoringFeature = useFeature("lockMonitoring");

  let bgColor;
  let rowClassNames;

  if (hasLockMonitoringFeature) {
    if (blocked || rowData.snapshot.waitingForLock) {
      // blocked by something - yellow
      bgColor = "bg-[#fefce8]";
    } else if (blocking) {
      // only blocking ("root" query) - red
      bgColor = "bg-[#fef2f2]";
    }
    rowClassNames = classNames(bgColor, pid == hoveredPid && "font-bold");
  } else {
    if (rowData.snapshot.waitingForLock) {
      bgColor = "bg-[#fefce8]";
    }
    rowClassNames = bgColor;
  }

  return (
    <div
      title={title}
      className={classNames(className, "!mt-0", rowClassNames)}
    >
      {children}
    </div>
  );
};

const PidCellWithRef: React.FunctionComponent<{
  backend: Backend;
  databaseId: string;
}> = ({ backend, databaseId }) => {
  const { databaseBackend } = useRoutes();

  // handle focus in pid cell as scrollIntoView doesn't work with overflow-hidden
  // this cell uses overflow-visible instead (shouldn't overflow so safe to do so)
  const pidRef = useRef<HTMLAnchorElement>();
  const focus = useFocusedPid();
  const setFocus = useSetFocusedPid();

  useEffect(() => {
    if (backend.pid === focus && pidRef.current) {
      pidRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setFocus(null);
    }
  }, [backend.pid, focus, setFocus]);

  return (
    <Link
      to={databaseBackend(databaseId, backend.identity)}
      ref={pidRef}
      className="scroll-mt-[70px]"
    >
      {backend.pid}
    </Link>
  );
};

const QueryDetails: React.FunctionComponent<{
  snapshot: BackendSnapshot;
  backend: Backend;
  vacuumRun: VacuumRun | null;
  serverId: string;
  setPaused?: (value: boolean) => void;
}> = ({ snapshot, backend, vacuumRun, serverId, setPaused }) => {
  const [expanded, setExpanded] = useState(false);
  const handleToggleExpanded = () => {
    setPaused?.(true);
    setExpanded((currExp) => !currExp);
  };
  const hasLockMonitoringFeature = useFeature("lockMonitoring");
  const { serverVacuum } = useRoutes();

  let query;
  if (backend.backendType === "autovacuum worker") {
    // Autovacuum workers don't have a usable query text, so show the table
    // being vacuumed (from the matching vacuum run) instead
    const schemaTable = vacuumRun?.schemaTable ?? null;
    if (vacuumRun && schemaTable) {
      query = (
        <Link to={serverVacuum(serverId, vacuumRun.identity)}>
          autovacuum: VACUUM {schemaTable.schemaName}.{schemaTable.tableName}
        </Link>
      );
    } else {
      query = <>autovacuum: VACUUM (unknown table)</>;
    }
  } else if (snapshot.queryTextShort) {
    query = <SQL inline sql={snapshot.queryTextShort} />;
  } else {
    query = <>-</>;
  }

  const uniqueBlockedByCount = snapshot.uniqBlockedByPids.length;
  const uniqueBlockingCount = snapshot.uniqBlockingPids.length;
  const blocked = uniqueBlockedByCount > 0;
  const blocking = uniqueBlockingCount > 0;
  let toggleButton;
  let lockBadge;

  if (hasLockMonitoringFeature) {
    if (blocked || blocking) {
      toggleButton = (
        <Button
          bare
          aria-label={
            expanded ? "Collapse lock details" : "Expand lock details"
          }
          onClick={handleToggleExpanded}
          className="w-3 mr-2"
        >
          <ExpandToggle expanded={expanded} />
        </Button>
      );
    }

    if (blocked) {
      // yellow
      lockBadge = (
        <Badge className="!inline-flex items-center gap-[3px] !bg-[#fde047] !text-[#713f12]">
          <FontAwesomeIcon icon={faClock} />
          {uniqueBlockedByCount}
        </Badge>
      );
    } else if (blocking) {
      // red
      lockBadge = (
        <Badge className="!inline-flex items-center gap-[3px] !bg-[#fca5a5] !text-[#7f1d1d]">
          <FontAwesomeIcon icon={faLockSolid} />
          {uniqueBlockingCount}
        </Badge>
      );
    }
  }

  const blockedByInfo = blocked && (
    <>
      <div className="font-medium">Waiting For</div>
      <div className="text-ellipsis overflow-hidden">
        <PidList pidList={snapshot.uniqBlockedByPids} />
      </div>
    </>
  );

  const blockingInfo = blocking && (
    <>
      <div className="font-medium">Blocking</div>
      <div className="text-ellipsis overflow-hidden">
        <PidList pidList={snapshot.uniqBlockingPids} />
      </div>
    </>
  );

  return (
    <div>
      {toggleButton}
      {lockBadge && <>{lockBadge} </>}
      {query}
      {expanded && (
        <div className="ml-5">
          <div className="grid grid-cols-[100px_1fr] gap-1 mt-3">
            {blockedByInfo}
            {blockingInfo}
          </div>
        </div>
      )}
    </div>
  );
};

const PidList: React.FunctionComponent<{
  pidList: number[];
}> = ({ pidList }) => {
  const setHoveredPid = useSetHoveredPid();
  const setFocusedPid = useSetFocusedPid();

  const handlePidHover = (pid: number, enter: boolean) => {
    if (enter) {
      setHoveredPid(pid);
    } else {
      setHoveredPid(null);
    }
  };
  const handlePidFocus = (pid: number) => {
    setFocusedPid(pid);
  };

  return (
    <>
      {pidList.map((pid, i) => [
        i > 0 && ", ",
        <Button
          bare
          key={i}
          onMouseEnter={() => handlePidHover(pid, true)}
          onMouseLeave={() => handlePidHover(pid, false)}
          onClick={() => handlePidFocus(pid)}
          className="underline hover:cursor-pointer hover:text-[#3272af]"
        >
          {pid}
        </Button>,
      ])}
    </>
  );
};

// Context to pass hovered and focused pid information
const HoveredPidContext = React.createContext<number | null>(null);
const SetHoveredPidContext = React.createContext<(s: number | null) => void>(
  () => {
    /* do nothing */
  },
);

export const WithPidHover = ({ children }: { children: React.ReactNode }) => {
  const [hoveredPid, setHoveredPid] = useState<number | null>(null);
  return (
    <SetHoveredPidContext.Provider value={setHoveredPid}>
      <HoveredPidContext.Provider value={hoveredPid}>
        {children}
      </HoveredPidContext.Provider>
    </SetHoveredPidContext.Provider>
  );
};

function useHoveredPid(): number | null {
  return useContext(HoveredPidContext);
}

function useSetHoveredPid(): (val: number | null) => void {
  return useContext(SetHoveredPidContext);
}

const FocusedPidContext = React.createContext<number | null>(null);
const SetFocusedPidContext = React.createContext<(s: number | null) => void>(
  () => {
    /* do nothing */
  },
);

export const WithPidFocus = ({ children }: { children: React.ReactNode }) => {
  const [focusedPid, setFocusedPid] = useState<number | null>(null);
  return (
    <SetFocusedPidContext.Provider value={setFocusedPid}>
      <FocusedPidContext.Provider value={focusedPid}>
        {children}
      </FocusedPidContext.Provider>
    </SetFocusedPidContext.Provider>
  );
};

function useFocusedPid(): number | null {
  return useContext(FocusedPidContext);
}

function useSetFocusedPid(): (val: number | null) => void {
  return useContext(SetFocusedPidContext);
}
export default Activity;
